import { useCallback, useRef } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import "./index.css";

import enthooImg from "../shared/enthoo-pro-2-server-edition-2.webp";
import mes2xImg from "../shared/mes2x-pc.webp";
import rm600Img from "../shared/silverstone-rm600.webp";

const BUILDS = [
  {
    id: "enthoo",
    name: "Enthoo Pro 2 Server Edition",
    tagline: "Server-Class Custom AI Workstation",
    blurb:
      "Server-grade build for sustained multi-GPU training, large model fine-tuning, and 24/7 deep learning pipelines. Configure dual or quad NVIDIA RTX Pro GPUs, Threadripper PRO CPUs, ECC memory, and high-capacity NVMe storage.",
    image: enthooImg,
    href: "https://arsenalpc.com/product/custom-ai-workstations/enthoo-pro-2-server-edition-custom-ai-workstation/"
  },
  {
    id: "mes2x",
    name: "Meshify 2XL",
    tagline: "Dual-GPU Liquid-Cooled AI Workstation",
    blurb:
      "Dual graphics card AI workstation with custom liquid cooling, tuned for local LLM inference, fine-tuning, and generative AI workflows. Run dual RTX 5090 or RTX Pro 6000 configurations for RAG pipelines and Stable Diffusion training.",
    image: mes2xImg,
    href: "https://arsenalpc.com/product/custom-ai-workstations/mes2x-dual-gpu-ai-custom-workstation/"
  },
  {
    id: "rm600",
    name: "SilverStone RM600",
    tagline: "6U Rack-Mount Dual-GPU AI Workstation",
    blurb:
      "Rack-ready 6U build that mounts in a 19″ server rack or stands vertically at your desk. Configure dual RTX 5090 or RTX Pro 6000 Blackwell GPUs with up to 256 GB DDR5 for model training, inference, and rendering pipelines.",
    image: rm600Img,
    href: "https://arsenalpc.com/product/custom-ai-workstations/silverstone-rm600-custom-ai-workstation/"
  }
];

function ChevronIcon({ flip = false }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="22"
      height="22"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={flip ? { transform: "scaleX(-1)" } : undefined}
    >
      <polyline points="9 6 15 12 9 18" />
    </svg>
  );
}

export default function App() {
  const rootRef = useRef(null);
  const scrollerRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();
  const inView = useInView(rootRef, { margin: "-10% 0px", once: true });
  const shouldAnimate = inView && !prefersReducedMotion;

  // Carousel arrows loop: past the last card "next" wraps to the first,
  // before the first card "prev" wraps to the last — both always active.
  // (Arrows only render below the 3-across breakpoint via CSS.)
  const scrollByCard = useCallback(
    (dir) => {
      const el = scrollerRef.current;
      if (!el) return;
      const card = el.querySelector(".pb-card");
      const gap = parseFloat(getComputedStyle(el).columnGap || "20") || 20;
      const step = card ? card.offsetWidth + gap : el.clientWidth * 0.85;
      const behavior = prefersReducedMotion ? "auto" : "smooth";
      const maxLeft = el.scrollWidth - el.clientWidth;
      const atEnd = el.scrollLeft >= maxLeft - 2;
      const atStart = el.scrollLeft <= 2;

      if (dir > 0 && atEnd) {
        el.scrollTo({ left: 0, behavior });
      } else if (dir < 0 && atStart) {
        el.scrollTo({ left: maxLeft, behavior });
      } else {
        el.scrollBy({ left: dir * step, behavior });
      }
    },
    [prefersReducedMotion]
  );

  const fade = (delay = 0) => ({
    initial: { opacity: 0, y: 18 },
    animate: shouldAnimate ? { opacity: 1, y: 0 } : { opacity: 1, y: 0 },
    transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }
  });

  return (
    <section ref={rootRef} id="customize" className="pb">
      <div className="pb-inner">
        <div className="pb-header">
          <motion.span className="pb-eyebrow" {...fade(0)}>
            <span className="pb-eyebrow-mark" />
            Custom-Built AI Workstations
          </motion.span>
          <motion.h2 className="pb-title" {...fade(0.1)}>
            Configure the AI Workstation built for{" "}
            <span className="pb-title-accent">your workload</span>
          </motion.h2>
        </div>

        <div className="pb-carousel">
          <button
            type="button"
            className="pb-nav pb-nav-prev"
            aria-label="Previous build"
            onClick={() => scrollByCard(-1)}
          >
            <ChevronIcon flip />
          </button>

          <div className="pb-grid" ref={scrollerRef}>
            {BUILDS.map((build, i) => (
              <motion.a
                key={build.id}
                className="pb-card"
                href={build.href}
                initial={{ opacity: 0, y: 24 }}
                animate={
                  shouldAnimate
                    ? { opacity: 1, y: 0 }
                    : { opacity: 1, y: 0 }
                }
                transition={{
                  duration: 0.7,
                  delay: 0.3 + i * 0.12,
                  ease: [0.22, 1, 0.36, 1]
                }}
              >
                <div className="pb-card-image-wrap">
                  <div className="pb-card-glow" aria-hidden="true" />
                  <img
                    src={build.image}
                    alt={`${build.name} ${build.tagline}`}
                    className="pb-card-image"
                    loading="lazy"
                  />
                </div>

                <div className="pb-card-body">
                  <div className="pb-card-text">
                    <h3 className="pb-card-name">{build.name}</h3>
                    <p className="pb-card-tagline">{build.tagline}</p>
                    <p className="pb-card-blurb">{build.blurb}</p>
                  </div>

                  <span className="pb-card-cta">
                    Customize
                    <span className="pb-card-cta-arrow" aria-hidden="true">→</span>
                  </span>
                </div>
              </motion.a>
            ))}
          </div>

          <button
            type="button"
            className="pb-nav pb-nav-next"
            aria-label="Next build"
            onClick={() => scrollByCard(1)}
          >
            <ChevronIcon />
          </button>
        </div>
      </div>
    </section>
  );
}
